/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import Tooltip from '../components/tooltip/Tooltip';
import {useIsOverflowing} from '../hooks/useIsOverflowing';
import {DragIcon} from '../icons/current/DragIcon';
import {ThunderIcon} from '../icons/current/ThunderIcon';

const useStyles = makeStyles((theme: DLSTheme) => ({
  tipsContainer : {
    alignItems                     : 'center',
    display                        : 'flex',
    height                         : '32px',
    padding                        : '8px 8px 8px 16px',
    minWidth                       : '0',
    width                          : 'fit-content',
    [theme.breakpoints.down('sm')] : {
      display : 'none'
    }
  },
  tipsLabel : {
    color         : dlsColor('grey3'),
    display       : 'flex',
    fontSize      : '10px',
    alignItems    : 'center',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '100%',
    letterSpacing : '1px',
    textTransform : 'uppercase'
  },
  usageText : {
    color         : dlsColor('grey3'),
    display       : 'block',
    fontSize      : '12px',
    overflow      : 'hidden',
    alignItems    : 'center',
    fontFamily    : 'Roboto',
    fontWeight    : 500,
    lineHeight    : '100%',
    letterSpacing : '0.5px',
    '& svg'       : {
      display       : 'inline-flex',
      verticalAlign : 'middle'
    }
  },
  currentTips : {
    alignItems   : 'center',
    display      : 'flex',
    height       : '16px',
    overflow     : 'hidden',
    whiteSpace   : 'nowrap',
    textOverflow : 'ellipsis'
  },
  tipGroup : {
    alignItems           : 'center',
    display              : 'inline-flex',
    height               : '16px',
    padding              : 0,
    verticalAlign        : 'middle',
    '&:not(:last-child)' : {
      marginRight : '4px'
    }
  },
  tipText : {
    alignItems    : 'center',
    color         : dlsColor('grey3'),
    display       : 'block',
    fontFamily    : 'Roboto',
    fontSize      : '12px',
    fontWeight    : 500,
    lineHeight    : '14px',
    letterSpacing : '0.5px'
  },
  dragIcon : {
    alignItems     : 'center',
    display        : 'flex',
    height         : '16px',
    justifyContent : 'center',
    width          : '16px'
  },
  thunderIcon : {
    alignItems     : 'center',
    display        : 'flex',
    height         : '16px',
    justifyContent : 'center',
    width          : '16px'
  }
}));

/**
 * PaginationTips provides a localized informational string within the grid 
 * pagination footer. It supports icon interpolation for common grid 
 * actions like quick-filtering and column dragging.
 *
 * ```typescript
 *
 * import {PaginationTips} from '@ion/dls/src/components/grid/PaginationTips';
 *
 *   <PaginationTips />
 *
 * ```
 * 
 */
const PaginationTips = () => {
  const classes       = useStyles();
  const tipsRef       = useRef<HTMLDivElement>(null);
  const prefix        = translate('grids:pagination.tipsRearrangePrefix');
  const suffix        = translate('grids:pagination.tipsRearrangeSuffix');
  const resize        = translate('grids:pagination.tipsResize');
  const isOverflowing = useIsOverflowing(tipsRef, [prefix, suffix, resize]);

  // builds the tip content - isTooltip swaps each icon to white and drops
  // the local text color so it reads correctly against the Tooltip's own
  // dark bubble, instead of duplicating this markup for each context ...
  const renderTips = function(isTooltip: boolean) {
    const iconProps     = isTooltip ? {color: dlsColor('white')} : {};
    const textClassName = isTooltip ? '' : classes.tipText;
    return (
      <>
        <div className={classes.tipGroup}>
          <span className={classes.thunderIcon}>
            <ThunderIcon {...iconProps} />
          </span>
          <span className={textClassName}>
            {prefix}
          </span>
          <span className={classes.dragIcon}>
            <DragIcon {...iconProps} />
          </span>
          <span className={textClassName}>
            {suffix}
          </span>
        </div>
        <div className={classes.tipGroup}>
          <span className={classes.thunderIcon}>
            <ThunderIcon {...iconProps} />
          </span>
          <span className={textClassName}>
            {resize}
          </span>
        </div>
      </>
    );
  };

  return (
    <div className={classes.tipsContainer}>
      <Tooltip title={renderTips(true)} placement='top' arrow renderOnlyChildren={!isOverflowing}>
        <div ref={tipsRef} className={classes.currentTips}>
          {renderTips(false)}
        </div>
      </Tooltip>
    </div>
  );
};

export default React.memo(PaginationTips);
